import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { CompanyContext } from '../contexts/CompanyContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Zap, Sparkles, CheckCircle2, Lock, Crown, TrendingUp, Settings as SettingsIcon, DollarSign, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CURRENCIES } from '../lib/currencies';

const FEATURE_LABELS = {
  core: { label: 'Core FM', icon: Zap, desc: 'Dashboard, Buildings, Assets, Complaints, PPM, Staff, Notices, SOPs' },
  accounting: { label: 'Accounting & Finance', icon: DollarSign, desc: 'Invoices, bills, payments, A/R + A/P aging' },
  hr: { label: 'HR & Payroll', icon: CheckCircle2, desc: 'Leave requests, payroll records, approvals' },
  ai_insights: { label: 'AI Insights', icon: Sparkles, desc: 'Claude Sonnet predictive maintenance + report generation' },
  amc_contracts: { label: 'AMC Contracts & Provider KPI', icon: TrendingUp, desc: 'Annual maintenance contracts and supplier scorecards' },
  tenant_portal: { label: 'Tenant Portal', icon: CheckCircle2, desc: 'Tenant self-serve: complaints, utility readings' },
  advanced_analytics: { label: 'Advanced Analytics', icon: TrendingUp, desc: 'Drill-down charts, chronic-issue detection, SLA dashboards' },
  custom_reports: { label: 'Custom Reports', icon: CheckCircle2, desc: 'Build-your-own PDF reports with filter chips' },
};

const PLAN_META = {
  starter:    { color: 'slate', label: 'Starter', tagline: 'Essentials for a single small site' },
  standard:   { color: 'sky',   label: 'Standard', tagline: 'Core FM + compliance + tenant portal' },
  pro:        { color: 'amber', label: 'Pro', tagline: 'Standard + HR + AI insights' },
  enterprise: { color: 'violet', label: 'Enterprise', tagline: 'Everything — accounting, analytics, unlimited' },
};

export default function Subscription() {
  const { user } = useContext(AuthContext);
  const { company, refresh } = useContext(CompanyContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [featureKeys, setFeatureKeys] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [features, setFeatures] = useState({});
  const [currency, setCurrency] = useState('AED');
  const [currencySymbol, setCurrencySymbol] = useState('AED');
  const [saving, setSaving] = useState(false);
  const [prices, setPrices] = useState({}); // { starter: 0, standard: 29, ... }
  const [upgradingTo, setUpgradingTo] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);

  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const canEditFeatures = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'].includes(user?.role);
  const canUpgrade = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'].includes(user?.role);

  useEffect(() => {
    axios.get(`${API}/plans`, { withCredentials: true })
      .then(r => { setPlans(r.data.plans || []); setFeatureKeys(r.data.feature_keys || []); })
      .catch(() => {});
    axios.get(`${API}/billing/prices`, { withCredentials: true })
      .then(r => {
        const map = {};
        (r.data.plans || []).forEach(p => { map[p.key] = p.amount_usd; });
        setPrices(map);
      })
      .catch(() => {});
  }, []);

  // Detect Stripe redirect (?session_id=...) and poll status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');
    const cancelled = params.get('cancelled');
    if (cancelled) {
      toast.info('Upgrade cancelled');
      navigate('/subscription', { replace: true });
      return;
    }
    if (!sid) return;
    setPollingSession(sid);
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/billing/status/${sid}`, { withCredentials: true });
        if (r.data.payment_status === 'paid') {
          toast.success(`Plan upgraded to ${r.data.plan_key.toUpperCase()}`);
          setPollingSession(null);
          await refresh?.();
          navigate('/subscription', { replace: true });
          return;
        }
        if (r.data.status === 'expired') {
          toast.error('Payment session expired');
          setPollingSession(null);
          navigate('/subscription', { replace: true });
          return;
        }
        if (attempts >= maxAttempts) {
          toast.warning('Payment status check timed out — please refresh in a moment.');
          setPollingSession(null);
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        toast.error('Could not verify payment status');
        setPollingSession(null);
      }
    };
    poll();
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    if (!company) return;
    setSelectedPlan(company.plan || 'standard');
    setFeatures(company.features || {});
    setCurrency(company.currency || 'AED');
    setCurrencySymbol(company.currency_symbol || 'AED');
  }, [company]);

  const allowedKeysFor = (planKey) => {
    const p = plans.find(pp => pp.key === planKey);
    return new Set(p?.allowed_features || []);
  };

  const handlePlanChange = (newPlan) => {
    setSelectedPlan(newPlan);
    // Cascade — reset features to what the new plan offers
    const allowed = allowedKeysFor(newPlan);
    const next = {};
    featureKeys.forEach(k => { next[k] = allowed.has(k); });
    setFeatures(next);
  };

  const toggleFeature = (key) => {
    const allowed = allowedKeysFor(selectedPlan);
    if (!allowed.has(key)) {
      toast.error(`"${FEATURE_LABELS[key]?.label}" is not available on the ${selectedPlan} plan — upgrade to enable.`);
      return;
    }
    if (key === 'core') return; // always on
    setFeatures(f => ({ ...f, [key]: !f[key] }));
  };

  const save = async () => {
    if (!company) return;
    setSaving(true);
    try {
      const payload = { features, currency, currency_symbol: currencySymbol };
      if (isSuperAdmin) payload.plan = selectedPlan;
      await axios.put(`${API}/companies/${company.id}`, payload, { withCredentials: true });
      toast.success('Subscription settings saved');
      await refresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const startUpgrade = async (planKey) => {
    if (!canUpgrade) {
      toast.error('Only company admins can upgrade');
      return;
    }
    if (!prices[planKey] || prices[planKey] <= 0) {
      toast.info('That plan is free — choose a paid plan to upgrade.');
      return;
    }
    setUpgradingTo(planKey);
    try {
      const r = await axios.post(
        `${API}/billing/checkout`,
        { plan_key: planKey, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not start checkout');
      setUpgradingTo(null);
    }
  };

  return (
    <Layout>
      <div data-testid="subscription-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-600" />Subscription & Features
          </h1>
          <p className="text-sm text-muted-foreground">Manage your plan, toggle available features, and set the currency used across invoices, payroll, purchases, and reports.</p>
        </div>

        {/* Stripe payment polling banner */}
        {pollingSession && (
          <Card data-testid="payment-polling-banner" className="p-4 bg-amber-50 border-amber-300 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-amber-700 animate-spin" />
            <div className="flex-1">
              <div className="font-semibold text-foreground">Verifying your payment…</div>
              <div className="text-xs text-slate-600">Don't close this tab — we'll activate your new plan shortly.</div>
            </div>
          </Card>
        )}

        {/* Plan cards */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground">Subscription Plan</h2>
            {!isSuperAdmin && <span className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" />Only platform admin can change the plan</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {plans.map(p => {
              const meta = PLAN_META[p.key] || { color: 'slate', label: p.key, tagline: '' };
              const active = selectedPlan === p.key;
              const isCurrent = (company?.plan || 'standard') === p.key;
              const price = prices[p.key];
              const isPaid = price && price > 0;
              return (
                <div
                  key={p.key}
                  data-testid={`plan-${p.key}`}
                  className={`flex flex-col text-left p-4 rounded-xl border-2 transition ${
                    active ? `border-amber-500 bg-amber-50 shadow` : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <button
                    type="button"
                    disabled={!isSuperAdmin}
                    onClick={() => handlePlanChange(p.key)}
                    className={`text-left ${!isSuperAdmin ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-foreground capitalize">{meta.label}</span>
                      {active && <CheckCircle2 className="w-4 h-4 text-amber-600" />}
                    </div>
                    <p className="text-xs text-slate-600 mt-1 min-h-[32px]">{meta.tagline}</p>
                    <div className="mt-3">
                      {price === 0 ? (
                        <span className="text-2xl font-extrabold text-slate-900">Free</span>
                      ) : price ? (
                        <>
                          <span className="text-2xl font-extrabold text-slate-900">${price}</span>
                          <span className="text-xs text-muted-foreground"> / month</span>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-2">{p.allowed_features.length} features</div>
                  </button>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    {isCurrent ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" />Current plan
                      </span>
                    ) : isPaid && canUpgrade ? (
                      <Button
                        data-testid={`upgrade-${p.key}-btn`}
                        size="sm"
                        onClick={() => startUpgrade(p.key)}
                        disabled={upgradingTo === p.key}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                      >
                        {upgradingTo === p.key ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Redirecting…</>
                        ) : (
                          <><CreditCard className="w-3.5 h-3.5 mr-1" />Upgrade</>
                        )}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">{!canUpgrade ? 'Admin only' : ''}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Feature toggles */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground">Available Features</h2>
            <span className="text-xs text-muted-foreground">Enable or disable any feature included in your <strong className="capitalize">{selectedPlan}</strong> plan. Locked features require a plan upgrade.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {featureKeys.map(key => {
              const meta = FEATURE_LABELS[key] || { label: key, desc: '', icon: CheckCircle2 };
              const Icon = meta.icon;
              const allowed = allowedKeysFor(selectedPlan).has(key);
              const on = features[key];
              const locked = !allowed;
              return (
                <div
                  key={key}
                  className={`p-4 rounded-xl border flex items-start gap-3 ${
                    locked ? 'bg-slate-50 border-slate-200 opacity-70' :
                    on ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${on && !locked ? 'bg-emerald-500 text-white' : locked ? 'bg-slate-200 text-slate-500' : 'bg-slate-100 text-slate-600'}`}>
                    {locked ? <Lock className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-foreground">{meta.label}</div>
                      <label className={`inline-flex items-center gap-2 ${locked || !canEditFeatures || key === 'core' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          data-testid={`feature-toggle-${key}`}
                          checked={!!on && !locked}
                          disabled={locked || !canEditFeatures || key === 'core'}
                          onChange={() => toggleFeature(key)}
                          className="sr-only peer"
                        />
                        <span className="relative inline-block w-10 h-5 bg-slate-300 peer-checked:bg-emerald-500 rounded-full transition peer-disabled:bg-slate-200 before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-4 before:h-4 before:bg-white before:rounded-full before:transition peer-checked:before:translate-x-5"></span>
                      </label>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">{meta.desc}</p>
                    {locked && <p className="text-[11px] text-rose-600 mt-1">Upgrade to unlock</p>}
                    {key === 'core' && <p className="text-[11px] text-emerald-700 mt-1">Always on</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Currency settings */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground">Currency</h2>
            <span className="text-xs text-muted-foreground">Used across invoices, bills, payroll, stock, purchases, and reports.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Currency code</label>
              <Select value={currency} onValueChange={(v) => { const sym = CURRENCIES.find(c => c[0] === v)?.[1] || v; setCurrency(v); setCurrencySymbol(sym); }}>
                <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(([c, s]) => <SelectItem key={c} value={c}>{c} ({s})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Preview</label>
              <div data-testid="currency-preview" className="mt-1 px-4 py-2.5 rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 font-mono font-bold text-amber-900 text-lg">
                {currencySymbol} 12,345.67
              </div>
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button data-testid="subscription-save-btn" onClick={save} disabled={saving || !canEditFeatures} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
